import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Mail, ArrowLeft } from "lucide-react";
import { LOGO } from "@/data/site";
import { API, inputClass, labelClass, primaryBtn } from "./shared";

const Shell = ({ title, sub, children }) => (
  <main className="flex min-h-screen items-center justify-center bg-mint px-5 pt-20">
    <div className="w-full max-w-md rounded-3xl border border-brand/10 bg-white p-10 shadow-2xl shadow-brand/10">
      <img src={LOGO} alt="GROUPE AUTO LUXE" className="mb-8 h-16 w-auto" />
      <h1 className="font-display text-2xl font-extrabold text-forest">{title}</h1>
      <p className="mt-2 text-sm text-brand-hover">{sub}</p>
      {children}
    </div>
  </main>
);

export const ForgotPassword = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      toast.error("Impossible d'envoyer l'email. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Accès oublié ?" sub="Indiquez votre email de responsable : vous recevrez un lien valable 1 heure pour définir un nouveau mot de passe.">
      <div data-testid="forgot-password-page">
        {sent ? (
          <p className="mt-6 rounded-2xl bg-mint px-5 py-4 text-sm text-forest" data-testid="forgot-password-sent">
            Si un compte existe pour <b>{email}</b>, un email vient d'être envoyé. Vérifiez aussi vos spams.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-6">
            <label className={labelClass}>Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-password-email-input" className={inputClass} placeholder="vous@groupeautoluxeci.com" />
            <button type="submit" disabled={loading} data-testid="forgot-password-submit-button" className={`mt-6 w-full justify-center ${primaryBtn}`}>
              <Mail className="h-4 w-4" />
              {loading ? "Envoi..." : "Recevoir le lien"}
            </button>
          </form>
        )}
        <button type="button" onClick={onBack} data-testid="forgot-password-back-button" className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-brand hover:text-gold">
          <ArrowLeft className="h-3.5 w-3.5" /> Retour à la connexion
        </button>
      </div>
    </Shell>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) return toast.error("Les deux mots de passe ne correspondent pas");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: form.new_password });
      toast.success("Mot de passe réinitialisé, connectez-vous");
      navigate("/admin", { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Lien invalide ou expiré");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Nouveau mot de passe" sub="Choisissez un mot de passe de 8 caractères minimum.">
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-password-page">
        <div>
          <label className={labelClass}>Nouveau mot de passe</label>
          <input type="password" required minLength={8} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="reset-password-new-input" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Confirmer</label>
          <input type="password" required minLength={8} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="reset-password-confirm-input" className={inputClass} />
        </div>
        <button type="submit" disabled={loading || !token} data-testid="reset-password-submit-button" className={`w-full justify-center ${primaryBtn}`}>
          <KeyRound className="h-4 w-4" />
          {loading ? "Enregistrement..." : "Enregistrer"}
        </button>
        {!token && <p className="text-sm text-gold" data-testid="reset-password-missing-token">Lien incomplet : utilisez le lien reçu par email.</p>}
      </form>
    </Shell>
  );
};
